import { CC_NUMBER_REGEX, PERSONAL_INFO_COLLECTION_CURRENCIES } from "@constants/funding-collection";
import { Currency } from "@types";
import {
  CredentialModel,
  CredentialType,
  MemberModel,
  MemberSettingNameMode,
  MemberSettingPlayerInfoCollectionMode,
  MemberSettingPlayerNameCollectionMode,
  MemberUniqueInfo,
  MemberUniqueInfoCollection,
  UpdatePersonalInfoRequest,
} from "kz-ui-sdk";

export const buildPersonalInfoRequest = (
  v: {
    firstName: string;
    lastName: string;
    accountName: string;
    pan?: string;
    cpf?: string;
    curp?: string;
    pidType?: string;
    pidNumber?: string;
  },
  nameMode?: MemberSettingNameMode | null,
  existingInfo?: MemberUniqueInfo | null,
): UpdatePersonalInfoRequest => {
  const pidType = v.pidType;
  const pidNumber = v.pidNumber?.trim();
  const hasValidPidNumber = pidNumber && pidNumber.length > 0;

  // Build info object only with fields that have values
  const info: Record<string, string> = {};

  if (v.pan?.trim()) {
    info[MemberUniqueInfoCollection.PAN] = v.pan.trim();
  }
  if (v.cpf?.trim()) {
    info[MemberUniqueInfoCollection.CPF] = v.cpf.trim();
  }
  if (v.curp?.trim()) {
    info[MemberUniqueInfoCollection.CURP] = v.curp.trim();
  }

  // Map pidType to corresponding MemberUniqueInfoCollection enum value
  if (pidType && hasValidPidNumber) {
    switch (pidType) {
      case "dni":
        info[MemberUniqueInfoCollection.DNI] = pidNumber;
        break;
      case "ce":
        info[MemberUniqueInfoCollection.CE] = pidNumber;
        break;
      case "pen_pas":
        info[MemberUniqueInfoCollection.PEN_PAS] = pidNumber;
        break;
      case "ruc":
        info[MemberUniqueInfoCollection.RUC] = pidNumber;
        break;
      case "cc":
        info[MemberUniqueInfoCollection.CC] = pidNumber;
        break;
      case "nit":
        info[MemberUniqueInfoCollection.NIT] = pidNumber;
        break;
      case "cop_pas":
        info[MemberUniqueInfoCollection.COP_PAS] = pidNumber;
        break;
    }
  }

  // Build the request object, only including info if it has fields.
  // BE replaces member.info wholesale, so whenever we send info at all, carry the member's
  // existing entries (form values win on conflict). Name-only requests stay untouched.
  const hasInfo = Object.keys(info).length > 0;
  const mergedInfo = hasInfo ? mergeMemberInfo(existingInfo, info) : info;

  switch (nameMode) {
    case MemberSettingNameMode.FirstLast:
      return {
        firstname: v.firstName?.trim() || undefined,
        lastname: v.lastName?.trim() || undefined,
        ...(hasInfo && { info: mergedInfo }),
      };
    case MemberSettingNameMode.FullInFirst:
      return {
        firstname: v.accountName?.trim() || undefined,
        ...(hasInfo && { info: mergedInfo }),
      };
    default:
      return {
        firstname: v.firstName?.trim() || undefined,
        lastname: v.lastName?.trim() || undefined,
        ...(hasInfo && { info: mergedInfo }),
      };
  }
};

export const isGoogleCredential = (credential?: CredentialModel | null): boolean =>
  credential?.type === CredentialType.Google;

/**
 * Email/identity to DISPLAY. Google members show only the immutable google login email
 * (googleAccessKey virtual field, or the google credential's accessKey on old BE), never
 * the BO-editable member.email. Gating/prefill logic must keep reading member.email directly.
 */
export const getDisplayEmail = (
  member?: MemberModel | null,
  credential?: CredentialModel | null,
  opts?: { isGoogleLogin?: boolean; googleAccessKey?: string | null },
): string | undefined => {
  if (opts?.isGoogleLogin || isGoogleCredential(credential)) {
    return opts?.googleAccessKey || (isGoogleCredential(credential) ? credential?.accessKey : undefined) || undefined;
  }
  return member?.email || undefined;
};

/**
 * PID-group document collections (one-of identity documents), as opposed to
 * standalone unique-info fields (PAN/CPF/CURP) that are collected inline in funding forms.
 */
export const PID_GROUP_COLLECTIONS: MemberUniqueInfoCollection[] = [
  MemberUniqueInfoCollection.DNI,
  MemberUniqueInfoCollection.CE,
  MemberUniqueInfoCollection.PEN_PAS,
  MemberUniqueInfoCollection.RUC,
  MemberUniqueInfoCollection.CC,
  MemberUniqueInfoCollection.NIT,
  MemberUniqueInfoCollection.COP_PAS,
];

/**
 * Any stored value counts, except a malformed `cc`: it counts as missing so the player is asked again.
 * Keep other types presence-only, or players with older stored documents get re-prompted.
 */
const isInfoValueCollected = (
  memberInfo: MemberUniqueInfo | null | undefined,
  type: MemberUniqueInfoCollection,
): boolean => {
  const value = memberInfo?.[type];
  if (type !== MemberUniqueInfoCollection.CC) return !!value;
  // No trim: a stored " 123456 " is still malformed.
  return !!value && CC_NUMBER_REGEX.test(value);
};

export interface RequiredPersonalInfo {
  /** PID-group entries of the brand's playerInfoCollections, in config order. */
  requiredPidTypes: MemberUniqueInfoCollection[];
  /**
   * True when member.info holds a value for at least one CONFIGURED PID type
   * (PGW providers require the configured document; a stale off-config type does
   * not fulfil), or vacuously true when the brand configures no PID types.
   */
  isPersonalInfoCollected: boolean;
}

/**
 * kz-api's saveMemberInfo REPLACES member.info wholesale (`if (info) member.info = info`),
 * so a partial update silently drops every entry it omits. Carry the member's existing
 * non-empty entries into the request. Safe to re-send: the uniqueness check excludes the
 * member itself. A malformed stored `cc` is dropped so it can't fail the save.
 */
export const mergeMemberInfo = (
  memberInfo: MemberUniqueInfo | null | undefined,
  info: MemberUniqueInfo,
): MemberUniqueInfo => {
  const existing: MemberUniqueInfo = {};
  Object.entries(memberInfo ?? {}).forEach(([key, value]) => {
    if (typeof value !== "string" || !value.trim()) return;
    if (key === MemberUniqueInfoCollection.CC && !CC_NUMBER_REGEX.test(value)) return;
    existing[key as MemberUniqueInfoCollection] = value;
  });
  return { ...existing, ...info };
};

/**
 * Single source of truth for the post-login "required personal info" gate.
 * Shared by usePostLoginInfo, usePostLoginCheck, and useFundingProfileFormConfig
 * so the three consumers cannot drift.
 */
export const getRequiredPersonalInfo = (
  playerInfoCollections: MemberUniqueInfoCollection[] | undefined,
  memberInfo: MemberUniqueInfo | null | undefined,
): RequiredPersonalInfo => {
  const requiredPidTypes = (playerInfoCollections ?? []).filter((collection) =>
    PID_GROUP_COLLECTIONS.includes(collection),
  );
  const isPersonalInfoCollected =
    requiredPidTypes.length === 0 || requiredPidTypes.some((type) => isInfoValueCollected(memberInfo, type));
  return { requiredPidTypes, isPersonalInfoCollected };
};

/** Standalone unique-info docs collected as dedicated fields (vs the PID-type dropdown group). */
export const STANDALONE_INFO_COLLECTIONS: MemberUniqueInfoCollection[] = [
  MemberUniqueInfoCollection.PAN,
  MemberUniqueInfoCollection.CPF,
  MemberUniqueInfoCollection.CURP,
];

export type CollectionStage = "registration" | "login-nav" | "withdrawal";

interface IdCollectionModeInput {
  /** BE memberSettings.playerInfoCollectionMode; null/undefined = config absent -> legacy behavior. */
  infoCollectionMode?: MemberSettingPlayerInfoCollectionMode | null;
  currency?: string;
}

/**
 * Effective ID-collection mode. When BE has not shipped the config yet (absent field, stale
 * localStorage), fall back to the legacy hardcoded behavior: PEN collects at registration,
 * everywhere else the ID never blocks a stage. Delete the fallback (and
 * PERSONAL_INFO_COLLECTION_CURRENCIES) once every environment serves the config.
 */
const getEffectiveInfoCollectionMode = (input: IdCollectionModeInput): MemberSettingPlayerInfoCollectionMode | null => {
  if (input.infoCollectionMode) return input.infoCollectionMode;
  if (PERSONAL_INFO_COLLECTION_CURRENCIES.includes(input.currency as Currency)) {
    return MemberSettingPlayerInfoCollectionMode.Registration;
  }
  return null;
};

const ID_DUE_STAGES: Record<MemberSettingPlayerInfoCollectionMode, CollectionStage[]> = {
  [MemberSettingPlayerInfoCollectionMode.None]: [],
  [MemberSettingPlayerInfoCollectionMode.Registration]: ["registration", "login-nav"],
  [MemberSettingPlayerInfoCollectionMode.FirstWithdrawal]: ["withdrawal"],
};

/** Whether the ID document is owed at this stage (ignoring whether it is already collected). */
export const isIdCollectionDueAtStage = (stage: CollectionStage, input: IdCollectionModeInput): boolean => {
  const mode = getEffectiveInfoCollectionMode(input);
  // `?? []`: a mode value this bundle doesn't know (BE adds one later, stale cached MP) must
  // degrade to not-due, never crash the render path.
  return mode ? (ID_DUE_STAGES[mode] ?? []).includes(stage) : false;
};

/** Whether the name is owed at this stage (ignoring whether it is already collected). */
export const isNameCollectionDueAtStage = (
  stage: CollectionStage,
  nameCollectionMode?: MemberSettingPlayerNameCollectionMode | null,
): boolean => {
  switch (nameCollectionMode) {
    case MemberSettingPlayerNameCollectionMode.Registration:
      return stage === "registration" || stage === "login-nav";
    case MemberSettingPlayerNameCollectionMode.FirstWithdrawal:
      return stage === "withdrawal";
    default:
      return false; // FirstDeposit is owned by the deposit flow, untouched by this feature
  }
};

export interface PersonalInfoRequirementInput extends IdCollectionModeInput {
  nameCollectionMode?: MemberSettingPlayerNameCollectionMode | null;
  playerInfoCollections?: MemberUniqueInfoCollection[];
  /** Pass member state in explicitly: usePostLoginCheck runs on a fresh token before the store settles. */
  memberInfo?: MemberUniqueInfo | null;
  legalName?: string | null;
}

export interface PersonalInfoRequirement {
  /** ID owed at this stage and not yet present in member.info. */
  needsId: boolean;
  /** Name owed at this stage and the member has no legal name. */
  needsName: boolean;
  /** Docs collection forms may render. Empty when mode = none, even if BE keeps the list populated. */
  effectiveCollections: MemberUniqueInfoCollection[];
}

/**
 * The ticket's Name x ID collection matrix as code (KZ-6818). Single source of truth for
 * usePostLoginInfo, usePostLoginCheck, useWithdrawDrawer, useFundingProfileFormConfig and
 * PersonalInfoForm — consumers ask for their stage instead of doing currency math.
 * needsName at the withdrawal stage has no gate consumer by design: the withdraw hook opens
 * the drawer on needsId only, and the drawer then collects a missing name opportunistically.
 */
export const getPersonalInfoRequirement = (
  stage: CollectionStage,
  input: PersonalInfoRequirementInput,
): PersonalInfoRequirement => {
  const hasConfig = !!input.infoCollectionMode;

  const effectiveCollections =
    input.infoCollectionMode === MemberSettingPlayerInfoCollectionMode.None ? [] : input.playerInfoCollections ?? [];

  // Config present: any configured doc counts (PID group AND standalone cpf/curp/pan).
  // Legacy: keep the old PID-group-only check byte-identical.
  const isIdCollected = hasConfig
    ? effectiveCollections.length === 0 ||
      effectiveCollections.some((type) => isInfoValueCollected(input.memberInfo, type))
    : getRequiredPersonalInfo(input.playerInfoCollections, input.memberInfo).isPersonalInfoCollected;

  const needsId = isIdCollectionDueAtStage(stage, input) && !isIdCollected;

  // Legacy drawer behavior collected the name whenever it was missing, regardless of stage.
  const nameDue = hasConfig ? isNameCollectionDueAtStage(stage, input.nameCollectionMode) : true;
  const needsName = nameDue && !input.legalName;

  return { needsId, needsName, effectiveCollections };
};
